import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ElMessage } from 'element-plus';
import { useUserStore } from '@/store/modules/user';
import { ENV_CONFIG } from '@/config/env';
import { i18n } from '@/locales';

export interface ApiResp<T=unknown> { code: string | number; data: T; message?: string; }

// 根据环境配置基础 URL
const getBaseURL = () => {
  // 开发环境使用代理，不添加前缀
  if (import.meta.env.DEV) {
    return '/lincid';
  }
  console.log('ENV_CONFIG.API_BASE_URL;', ENV_CONFIG.API_BASE_URL)
  // 其他环境使用配置的 API 地址
  return ENV_CONFIG.API_BASE_URL;
};

const service: AxiosInstance = axios.create({
  baseURL: getBaseURL(),
  timeout: ENV_CONFIG.API_TIMEOUT
});

service.interceptors.request.use((config) => {
  const user = useUserStore();

  // 设置默认请求头
  config.headers = config.headers || {};

  // 如果是 FormData，不设置 Content-Type，让浏览器自动设置（包括 boundary）
  // 否则设置默认的 application/json
  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }

  // 设置语言请求头
  const currentLocale = typeof i18n.global.locale === 'string' ? i18n.global.locale : i18n.global.locale.value;
  if (currentLocale) {
    config.headers['Linc-i18nextLng'] = currentLocale;
  }
  if (!config.noBearer) {
    // 添加 token
    if (user.token) {
      (config.headers as any).Authorization = `Bearer ${user.token}`;
    }
  }
  
  if (user.userEnterpriseId) {
    (config.headers as any)['x-enterprise-id'] = `${user.userEnterpriseId}`;
    (config.headers as any)['x-rsep-id'] = `${user.userEnterpriseId}`;
  }
  // 超管视角不携带 ['x-product-id']
  // Commented out by xugy at 2026-01-07: remove productId header
  // if (user.productId) {
  //   (config.headers as any)['x-product-id'] = `${user.productId}`;
  // }
  // 开发环境打印请求信息
  if (import.meta.env.DEV) {
    // console.log('请求:', {
    //   method: config.method?.toUpperCase(),
    //   url: config.url,
    //   baseURL: config.baseURL,
    //   fullURL: (config.baseURL || '') + (config.url || ''),
    //   data: config.data instanceof FormData ? '[FormData]' : config.data
    // });
  }

  return config;
});

service.interceptors.response.use(
  (res: AxiosResponse<ApiResp>) => {
    // 如果响应是 Blob 类型（文件下载），直接返回
    if (res.data instanceof Blob) {
      return res.data as any;
    }

    const payload = res.data;
    const code = String(payload.code);

    // 判断是否为成功状态
    const isSuccess = code === '00000' || code === '0' || code === '200';

    if (!isSuccess) {
      const user = useUserStore();

      // Token 过期直接返回，不处理
      if (code === 'C0007' || code === '00002') {
        user.handleTokenExpired();
        return Promise.reject(payload);
      }

      // 如果是 401 错误，直接登出
      if (code === '401' || payload.code === 401) {
        user.logout();
      }

      return Promise.reject(payload);
    }

    // 成功时返回 data
    return payload.data as any;
  },
  (err) => {
    // 开发环境打印错误信息
    if (import.meta.env.DEV) {
      console.error('请求错误:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        url: err.config?.url,
        data: err.response?.data,
        message: err.message
      });
    }

    const user = useUserStore();

    // 检查响应数据中的 code
    const responseData = err.response?.data;
    if (responseData && responseData.code !== undefined) {
      const code = String(responseData.code);

      // Token 过期直接返回，不处理
      if (code === 'C0007' || code === '00002') {
        return Promise.reject(responseData);
      }
    }

    // 如果是 401 错误，直接登出
    if (err.response?.status === 401) {
      user.logout();
    }

    return Promise.reject(err);
  }
);

export function request<T=any>(config: AxiosRequestConfig & {noBearer?: boolean}) {
  return service.request<any, T>(config);
}

export default service;
